import { Box, ButtonBase, Chip, IconButton, Tooltip } from '@mui/material'
import Check from '@mui/icons-material/Check'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import React from 'react'
import Dialog from '../components/Dialog'
import updateRestaurantSlot from '../mutations/updateRestaurantSlot'
import { handleMutationError } from '../utils/errors'

import { RestaurantSlot } from '../utils/restaurantSlot'
import Button from './Button'
import CreateDialog from './CreateDialog'
import Txt from './Txt'
import Card from './Card'

type Day = RestaurantSlot['day']

const days: { day: Day; short: string }[] = [
	{ day: 'MONDAY', short: 'Mo' },
	{ day: 'TUESDAY', short: 'Di' },
	{ day: 'WEDNESDAY', short: 'Mi' },
	{ day: 'THURSDAY', short: 'Do' },
	{ day: 'FRIDAY', short: 'Fr' },
	{ day: 'SATURDAY', short: 'Sa' },
	{ day: 'SUNDAY', short: 'So' },
]
const allDays = days.map(({ day }) => day)
const weekdays = allDays.slice(0, 5)

type QuickAction = { label: string; days: Day[]; hour: number; minute: number }

const quickActions: QuickAction[] = [
	{ label: 'Täglich 18:00', days: allDays, hour: 18, minute: 0 },
	{ label: 'Täglich 17:30', days: allDays, hour: 17, minute: 30 },
	{ label: 'Täglich 19:00', days: allDays, hour: 19, minute: 0 },
	{ label: 'Mo–Fr 18:00', days: weekdays, hour: 18, minute: 0 },
	{ label: 'Fr–So 18:00', days: ['FRIDAY', 'SATURDAY', 'SUNDAY'], hour: 18, minute: 0 },
	{ label: 'Täglich 12:00', days: allDays, hour: 12, minute: 0 },
]

const timeConfig = [
	{
		label: 'Stunde',
		property: 'hour',
		type: 'select' as const,
		options: Array.from({ length: 24 }, (_, i) => i.toString()),
	},
	{
		label: 'Minute',
		property: 'minute',
		type: 'select' as const,
		options: ['00', '15', '30', '45'],
	},
]

const formatTime = (hour: number, minute: number) =>
	`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

// Fasst die aktiven Tage einer Uhrzeit lesbar zusammen: "täglich", "Mo–Fr", "täglich außer Mo", "Mo, Mi".
const summarizeDays = (activeDays: Day[]) => {
	const indexes = activeDays.map((day) => allDays.indexOf(day)).sort((a, b) => a - b)
	const missing = days.filter(({ day }) => !activeDays.includes(day))
	return indexes.length === 7
		? 'täglich'
		: indexes.length === 6
			? `täglich außer ${missing[0].short}`
			: indexes.length > 2 && indexes[indexes.length - 1] - indexes[0] === indexes.length - 1
				? `${days[indexes[0]].short}–${days[indexes[indexes.length - 1]].short}`
				: indexes.map((index) => days[index].short).join(', ')
}

const sxHeader = {
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	gap: '1rem',
} as const
const sxHeaderButtons = { display: 'flex', gap: '0.5rem' } as const
const sxQuickActions = {
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: '0.5rem',
	mt: '1.25rem',
} as const
const sxGrid = {
	display: 'grid',
	gridTemplateColumns: '6.5rem repeat(7, minmax(2.75rem, 1fr)) 2.5rem',
	columnGap: '0.5rem',
	rowGap: '0.5rem',
	alignItems: 'center',
	mt: '1.5rem',
	overflowX: 'auto',
} as const
const sxDayHeader = {
	textAlign: 'center',
	fontWeight: 'bold',
	color: 'text.secondary',
	pb: '0.25rem',
	borderBottom: '2px solid',
	borderColor: 'divider',
} as const
const sxCellBase = {
	height: '2.75rem',
	width: '100%',
	borderRadius: '0.6rem',
	transition: 'all 0.15s',
} as const
const sxCellActive = {
	...sxCellBase,
	bgcolor: 'primary.main',
	color: 'common.white',
	boxShadow: 1,
	'&:hover': { bgcolor: 'primary.dark' },
} as const
const sxCellInactive = {
	...sxCellBase,
	border: '1.5px dashed',
	borderColor: 'grey.300',
	color: 'transparent',
	'&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'action.hover' },
} as const
const sxEmpty = {
	mt: '1.5rem',
	p: '2rem',
	textAlign: 'center',
	border: '1.5px dashed',
	borderColor: 'grey.300',
	borderRadius: '0.75rem',
} as const

const SlotCell = React.memo(
	({
		day,
		hour,
		minute,
		slotId,
		onToggle,
	}: {
		day: Day
		hour: number
		minute: number
		slotId?: string
		onToggle: (args: { day: Day; hour: number; minute: number; slotId?: string }) => Promise<void>
	}): JSX.Element => {
		const [pending, setPending] = React.useState(false)
		const handleClick = React.useCallback(async () => {
			setPending(true)
			await onToggle({ day, hour, minute, slotId })
			setPending(false)
		}, [day, hour, minute, onToggle, slotId])
		const sx = React.useMemo(
			() => ({ ...(slotId ? sxCellActive : sxCellInactive), opacity: pending ? 0.5 : 1 }),
			[pending, slotId]
		)
		return (
			<ButtonBase sx={sx} onClick={handleClick} disabled={pending}>
				<Check fontSize="small" />
			</ButtonBase>
		)
	}
)

const QuickActionChip = React.memo(
	({
		quickAction,
		slots,
		onApply,
	}: {
		quickAction: QuickAction
		slots: RestaurantSlot[]
		onApply: (quickAction: QuickAction) => Promise<void>
	}): JSX.Element => {
		const handleClick = React.useCallback(() => onApply(quickAction), [onApply, quickAction])
		const complete = React.useMemo(
			() =>
				quickAction.days.every((day) =>
					slots.some(
						(slot) =>
							slot.day === day &&
							slot.hour === quickAction.hour &&
							slot.minute === quickAction.minute
					)
				),
			[quickAction, slots]
		)
		return (
			<Chip
				label={quickAction.label}
				variant="outlined"
				color="primary"
				onClick={handleClick}
				disabled={complete}
			/>
		)
	}
)

const TimeRow = React.memo(
	({
		hour,
		minute,
		slots,
		onToggle,
		onDeleteRow,
	}: {
		hour: number
		minute: number
		slots: RestaurantSlot[]
		onToggle: (args: { day: Day; hour: number; minute: number; slotId?: string }) => Promise<void>
		onDeleteRow: (rowSlots: RestaurantSlot[]) => void
	}): JSX.Element => {
		const handleDeleteRow = React.useCallback(() => onDeleteRow(slots), [onDeleteRow, slots])
		return (
			<>
				<Box>
					<Txt variant="h5" fontWeight="bold" m="0">
						{formatTime(hour, minute)}
					</Txt>
					<Txt variant="caption" color="text.secondary">
						{summarizeDays(slots.map((slot) => slot.day))}
					</Txt>
				</Box>
				{days.map(({ day }) => (
					<SlotCell
						key={day}
						day={day}
						hour={hour}
						minute={minute}
						slotId={slots.find((slot) => slot.day === day)?.id}
						onToggle={onToggle}
					/>
				))}
				<Tooltip title="Uhrzeit an allen Tagen löschen">
					<IconButton size="small" onClick={handleDeleteRow}>
						<DeleteOutline fontSize="small" />
					</IconButton>
				</Tooltip>
			</>
		)
	}
)

const EditSlotRules = ({
	slots,
	restaurantId,
}: {
	slots: RestaurantSlot[]
	restaurantId: string
}): JSX.Element => {
	const createSlots = React.useCallback(
		async ({ days: targetDays, hour, minute }: { days: Day[]; hour: number; minute: number }) => {
			const results = await Promise.all(
				targetDays
					.filter(
						(day) =>
							!slots.some(
								(slot) => slot.day === day && slot.hour === hour && slot.minute === minute
							)
					)
					.map((day) => updateRestaurantSlot({ type: 'CREATE', restaurantId, day, hour, minute }))
			)
			const failedResult = results.find((result) => result.errors)
			if (failedResult) {
				handleMutationError('updateRestaurantSlot', failedResult.errors)
			}
			return !failedResult
		},
		[restaurantId, slots]
	)

	const deleteSlots = React.useCallback(
		async (slotsToDelete: RestaurantSlot[]) => {
			const results = await Promise.all(
				slotsToDelete.map((slot) =>
					updateRestaurantSlot({ type: 'DELETE', restaurantId, slotId: slot.id })
				)
			)
			const failedResult = results.find((result) => result.errors)
			if (failedResult) {
				handleMutationError('updateRestaurantSlot', failedResult.errors)
			}
		},
		[restaurantId]
	)

	const confirmDelete = React.useCallback(
		(title: string, slotsToDelete: RestaurantSlot[]) => {
			Dialog.render({
				title,
				description:
					'Das Löschen von Regeln entfernt keine bereits erzeugten Lieferzeitpunkte. Diese bleiben bestehen.',
				buttons: [
					{ id: 'cancel', label: 'Abbrechen' },
					{
						id: 'delete',
						label: 'Löschen',
						variant: 'outlined',
						onClick: () => deleteSlots(slotsToDelete),
					},
				],
			})
		},
		[deleteSlots]
	)

	const handleDeleteAll = React.useCallback(
		() => confirmDelete('Alle Regeln löschen', slots),
		[confirmDelete, slots]
	)

	const handleDeleteRow = React.useCallback(
		(rowSlots: RestaurantSlot[]) =>
			confirmDelete(
				`Regeln für ${formatTime(rowSlots[0].hour, rowSlots[0].minute)} löschen`,
				rowSlots
			),
		[confirmDelete]
	)

	const handleToggle = React.useCallback(
		async ({
			day,
			hour,
			minute,
			slotId,
		}: {
			day: Day
			hour: number
			minute: number
			slotId?: string
		}) => {
			const { errors } = await updateRestaurantSlot(
				slotId
					? { type: 'DELETE', restaurantId, slotId }
					: { type: 'CREATE', restaurantId, day, hour, minute }
			)
			if (errors) {
				handleMutationError('updateRestaurantSlot', errors)
			}
		},
		[restaurantId]
	)

	const handleQuickAction = React.useCallback(
		async (quickAction: QuickAction) => {
			await createSlots(quickAction)
		},
		[createSlots]
	)

	const handleCreateTime = React.useCallback(
		async ({ data }: { data: Record<string, unknown> }) => {
			if (data?.hour === undefined || data?.minute === undefined) {
				handleMutationError('updateRestaurantSlot', 'Ungültiger Wert')
				return false
			}
			return createSlots({ days: allDays, hour: Number(data.hour), minute: Number(data.minute) })
		},
		[createSlots]
	)

	const handleOpenCreateDialog = React.useCallback(() => {
		Dialog.render({
			renderContent: ({ dismissPortal }) => (
				<CreateDialog
					title="Uhrzeit hinzufügen (an allen Tagen)"
					config={timeConfig}
					onSubmit={handleCreateTime}
					dismissPortal={dismissPortal}
				/>
			),
		})
	}, [handleCreateTime])

	const timeRows = React.useMemo(
		() =>
			[...new Set(slots.map((slot) => slot.hour * 60 + slot.minute))]
				.sort((a, b) => a - b)
				.map((minutesOfDay) => ({
					hour: Math.floor(minutesOfDay / 60),
					minute: minutesOfDay % 60,
					slots: slots.filter((slot) => slot.hour * 60 + slot.minute === minutesOfDay),
				})),
		[slots]
	)

	return (
		<Box maxWidth="100%">
			<Card mb="3rem">
				<Box sx={sxHeader}>
					<Txt variant="h4" fontWeight="bold" m="0" color="primary.main">
						Lieferzeitpunkte-Regeln
					</Txt>
					<Box sx={sxHeaderButtons}>
						<Button variant="outlined" startIconName="Add" onClick={handleOpenCreateDialog}>
							Uhrzeit hinzufügen
						</Button>
						<Button
							variant="outlined"
							color="error"
							startIconName="DeleteOutline"
							onClick={handleDeleteAll}
							disabled={slots.length === 0}
						>
							Alle löschen
						</Button>
					</Box>
				</Box>
				<Txt variant="body2" color="text.secondary" mt="0.35rem">
					Hier kann definiert werden, nach welchen Regeln das Restaurant regelmässig
					Lieferzeitpunkte bereitstellt. Entsprechend der Regeln werden die wöchentlichen
					Lieferzeitpunkte für die nächsten 2 Tage generiert. Kunden aus allen definierten
					Gemeinden können sich dann in diese Lieferzeitpunkte einbuchen.
				</Txt>
				<Box sx={sxQuickActions}>
					<Txt variant="body2" fontWeight="bold" color="text.secondary" mr="0.25rem">
						Schnellauswahl:
					</Txt>
					{quickActions.map((quickAction) => (
						<QuickActionChip
							key={quickAction.label}
							quickAction={quickAction}
							slots={slots}
							onApply={handleQuickAction}
						/>
					))}
				</Box>
				{timeRows.length === 0 ? (
					<Box sx={sxEmpty}>
						<Txt variant="body1" color="text.secondary">
							Noch keine Regeln. Wähle eine Schnellauswahl oder füge eine Uhrzeit hinzu.
						</Txt>
					</Box>
				) : (
					<Box sx={sxGrid}>
						<Box />
						{days.map(({ day, short }) => (
							<Txt key={day} variant="body2" sx={sxDayHeader}>
								{short}
							</Txt>
						))}
						<Box />
						{timeRows.map((timeRow) => (
							<TimeRow
								key={`${timeRow.hour}:${timeRow.minute}`}
								hour={timeRow.hour}
								minute={timeRow.minute}
								slots={timeRow.slots}
								onToggle={handleToggle}
								onDeleteRow={handleDeleteRow}
							/>
						))}
					</Box>
				)}
			</Card>
		</Box>
	)
}

export default EditSlotRules
